import { MdArticle, MdFileCopy, MdOutlineForum, MdOutlineLogout, MdTroubleshoot } from '@oaknorth/component-core-v2';
import { useFlag } from '@oaknorth/util-feature-flags';
import { T } from '@oaknorth/util-intl';
import { InternalTextLink } from '@oaknorth/util-routing';
import { useOnLogout, useUserInfo } from '@oaknorth/util-user';

import { FeatureFlag } from '../constants';

import { ONClimateScorecardLogo } from './icons/ONClimateScorecardLogo';
import { ONPortfolioMonitoringLogo } from './icons/ONPortfolioMonitoringLogo';
import { ONTotalClimateLogo } from './icons/ONTotalClimateLogo';
import { ClientFeatureSpec, InternalFeatureSpec, UtilitiesSpec } from './types';

export function isNotNull<T>(value: T | null): value is T {
  return !(value === null);
}

export function useClientFeatures(): ClientFeatureSpec[] {
  const hasMonitoring = useFlag<boolean>(FeatureFlag.HAS_MONITORING);
  const hasClimate = useFlag<boolean>(FeatureFlag.HAS_CLIMATE);
  const hasScorecard = useFlag<boolean>(FeatureFlag.HAS_CLIMATE_SCORECARD);

  return [
    {
      name: T`homePage.mainFeatures.portfolioMonitoring.name`,
      key: 'portfolio-monitoring',
      to: '/monitoring',
      icon: ONPortfolioMonitoringLogo,
      description: T`homePage.mainFeatures.portfolioMonitoring.description`,
      additionalActions: (
        <InternalTextLink to="/monitoring/saved-alerts">{T`homePage.mainFeatures.portfolioMonitoring.alerts.link`}</InternalTextLink>
      ),
      isLocked: !hasMonitoring,
    },
    {
      name: T`homePage.mainFeatures.totalClimate.name`,
      key: 'climate',
      to: '/climate',
      icon: ONTotalClimateLogo,
      description: T`homePage.mainFeatures.totalClimate.description`,
      isLocked: !hasClimate,
    },
    {
      name: T`homePage.modules.climateScorecard.name`,
      key: 'climate-scorecard',
      to: '/climate-scorecard',
      icon: ONClimateScorecardLogo,
      description: T`homePage.modules.climateScorecard.description`,
      isLocked: !hasScorecard,
    },
  ];
}

export function useUtilities(): UtilitiesSpec[] {
  const showExcludedBorrowers = useFlag<boolean>(FeatureFlag.HAS_AUDIT_EXCLUDED_BORROWERS);
  const hasMonitoring = useFlag<boolean>(FeatureFlag.HAS_MONITORING);
  const hasClimate = useFlag<boolean>(FeatureFlag.HAS_CLIMATE);
  const showGeneralFileRepository = useFlag<boolean>(FeatureFlag.GENERAL_FILE_REPOSITORY);
  const showReleaseNotesLink = hasMonitoring || hasClimate;
  const onLogout = useOnLogout();
  return [
    showReleaseNotesLink
      ? {
          name: T`homePage.additionalLinks.releaseNotes.name`,
          key: 'release-notes',
          to: '/release-notes',
          icon: MdArticle,
        }
      : null,
    showExcludedBorrowers
      ? {
          name: T`homePage.additionalLinks.diagnostics.name`,
          key: 'diagnostics',
          to: '/diagnostics',
          icon: MdTroubleshoot,
        }
      : null,
    showGeneralFileRepository
      ? {
          name: T`homePage.additionalLinks.fileRepository.name`,
          key: 'file-repository',
          to: '/file-repository',
          icon: MdFileCopy,
        }
      : null,
    {
      name: T`homePage.additionalLinks.support.name`,
      key: 'support',
      to: '/support',
      icon: MdOutlineForum,
    },
    onLogout
      ? {
          name: T`homePage.additionalLinks.signOut.name`,
          key: 'signOut',
          icon: MdOutlineLogout,
          onClick: onLogout,
        }
      : null,
  ].filter(isNotNull);
}

export function useInternalFeatures(): InternalFeatureSpec[] | null {
  const user = useUserInfo();
  const hasCalc = useFlag<boolean>(FeatureFlag.HAS_CALC);
  const hasExplainability = useFlag<boolean>(FeatureFlag.HAS_EXPLAINABILITY);
  const hasScenarios = useFlag<boolean>(FeatureFlag.HAS_SCENARIOS);

  const isOnciuser = user?.isONUser;
  if (!isOnciuser) {
    return null;
  }
  return [
    {
      key: 'ingestion',
      name: T`homePage.internalFeautres.ingestion.name`,
      to: '/ingestion/file-connector/input-files',
    },
    {
      key: 'climateModelValidator',
      name: T`homePage.internalFeatures.climateModelValidator.name`,
      to: '/internal/climate-model-validator',
    },
    hasExplainability
      ? {
          key: 'explainability',
          name: T`homePage.internalFeatures.explainability.name`,
          to: '/internal/explainability',
        }
      : null,
    hasCalc ? { key: 'calc', name: T`homePage.internalFeatures.calc.name`, to: '/internal/calc' } : null,
    hasCalc
      ? { key: 'aggregation', name: T`homePage.internalFeatures.aggregation.name`, to: '/internal/aggregation' }
      : null,
    {
      key: 'scenarios',
      name: T`homePage.internalFeatures.scenarios.name`,
      subfeatures: [
        hasScenarios
          ? {
              key: 'onci-scenarios-webapp',
              name: T`homePage.internalFeatures.scenarios.app.name`,
              to: '/scenarios',
            }
          : null,
        {
          key: 'custom-stress-scenarios',
          name: T`homePage.internalFeatures.scenarios.customStressScenarios.name`,
          to: '/internal/custom-stress-scenarios',
        },
        {
          key: 'scenario-validation',
          name: T`homePage.internalFeatures.scenarios.scenarioValidation.name`,
          to: '/internal/scenario-validation',
        },
        {
          key: 'forecast-assumptions',
          name: T`homePage.internalFeatures.scenarios.forecastAssumptions.name`,
          to: '/internal/forecast-assumptions',
        },
        {
          key: 'cre-forecast-assumptions',
          name: T`homePage.internalFeatures.scenarios.creForecastAssumptions.name`,
          to: '/internal/cre-forecast-assumptions',
        },
      ].filter(isNotNull),
    },
  ].filter(isNotNull);
}
